import React, { useState } from "react";
import { Button, Card, Col, Row, Select, Table, Tag, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import type { ResizeCallbackData } from "react-resizable";
import { Resizable } from "react-resizable";
import { TasksGridVisualStore, TaskViewVisualStore } from "../stores";
import { TaskListModel, TaskMetadata } from "../types";
import { Observer, observer } from "mobx-react-lite";
import { useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import { ScrollToSections } from "../stores/TaskViewVisualStore";
import { Utils } from "src/modules/common/misc/Utils";
import TaskUserProfilePicture from "./TaskUserProfilePicture";
import ActorBadge from "./ActorBadge";
import { ColumnProps } from "antd/lib/table";

export enum ColumnKeys {
    Identifier = "identifier",
    Name = "name",
    Project = "projectId",
    Type = "taskType",
    Status = "status",
    DueDate = "dueDate",
    Attachments = "attachmentsCount",
    Owner = "createdBy",
    DateCreated = "createDate",
    AssignedTo = "assignedTo",
    Priority = "priority",
    SubTasks = "taskId",
    DoneTasks = "isDone",
}

export enum FilterOperator {
    Equal = "Equal",
    NotEqual = "NotEqual",
    Empty = "Empty",
}

const ResizableTitle = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    props: React.HTMLAttributes<any> & {
        onResize: (
            e: React.SyntheticEvent<Element>,
            data: ResizeCallbackData,
        ) => void;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onClick: (e: any) => void;
        width: number;
    },
) => {
    const { onResize, onClick, width, ...restProps } = props;

    if (!width) {
        return <th {...restProps} />;
    }

    return (
        <Resizable
            width={width}
            height={0}
            handle={<span className="react-resizable-handle" />}
            onResize={onResize}
            draggableOpts={{ enableUserSelectHack: false }}
        >
            <th
                {...restProps}
                onClick={(e) => {
                    if (
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        !(e.target as any).className.includes("react-resizable")
                    ) {
                        onClick(e);
                    }
                }}
            />
        </Resizable>
    );
};

type Props = {
    store: TasksGridVisualStore;
    taskPreviewStore: TaskViewVisualStore;
    headerRef: React.RefObject<HTMLDivElement>;
};

const TaskList: React.FC<Props> = ({ store, taskPreviewStore, headerRef }) => {
    const headerHeight = (headerRef.current?.offsetHeight ?? 0) + 164 || 0;

    const { widgetId, activityType, projectId, value } = useParams();
    const [searchParams] = useSearchParams();
    const activityPeriod = searchParams.get("period");
    const selectedTaskId = taskPreviewStore.taskPreview?.id;

    const hasSelectedText = () =>
        Boolean(window.getSelection()?.toString().trim());

    React.useEffect(() => {
        if (!store.layoutIsLoading && !store.isInitialized) {
            store.init(
                widgetId,
                activityType,
                activityPeriod,
                projectId,
                value,
            );
        }
    }, [store.layoutIsLoading]);

    React.useEffect(() => {
        const hasUnknownTaskType = store.tasks.some(
            (task) => !!task.taskType && !store.getTaskName(task.taskType),
        );

        if (hasUnknownTaskType) {
            store.loadTaskTypes();
        }
    }, [store, store.tasks, store.taskTypes.length]);

    const allCollumns: ColumnProps<TaskListModel>[] = [
        {
            title: "Identifier",
            dataIndex: "alphaId",
            width: 100,
            sorter: true,
            sortOrder:
                store.sortOrder.field === "alphaId"
                    ? store.sortOrder.order
                    : undefined,
            render: (alphaId: string, task: TaskListModel) => (
                <Button
                    key="task-id"
                    size="small"
                    type="link"
                    title={alphaId}
                    onClick={() => {
                        if (!hasSelectedText()) {
                            taskPreviewStore.togglePreview(task.id);
                        }
                    }}
                    className="entry-title task-button"
                >
                    <span className={"task-name"}>{alphaId}</span>
                </Button>
            ),
            onCell: () => ({ "data-id-cells": "tasks" }) as object,
        },
        {
            title: "Name",
            dataIndex: "name",
            filterSearch: true,
            width: 300,
            sorter: true,
            sortOrder:
                store.sortOrder.field === "name"
                    ? store.sortOrder.order
                    : undefined,
            render: (name: string, task: TaskListModel) => {
                const icons = getIcons(task);
                const getWidth = () => {
                    switch (icons.length) {
                        case 0:
                            return "100%";
                        case 1:
                            return "80%";
                        case 2:
                            return "70%";
                        case 3:
                            return "50%";
                        default:
                            return "100%";
                    }
                };
                return (
                    <>
                        <Button
                            key="task-name"
                            size="small"
                            type="link"
                            title={task.name}
                            onClick={() => {
                                if (!hasSelectedText()) {
                                    taskPreviewStore.togglePreview(task.id);
                                }
                            }}
                            className="entry-title task-button name"
                            style={{ maxWidth: getWidth() }}
                        >
                            <span className={"task-name"}>{name}</span>
                        </Button>
                        <div className="task-name-info-icons">{icons}</div>
                    </>
                );
            },
            onCell: () => ({ "data-id-cells": "name" }) as object,
        },
        {
            title: "Project",
            dataIndex: "projectId",
            width: 100,
            sorter: true,
            sortOrder:
                store.sortOrder.field === "projectId"
                    ? store.sortOrder.order
                    : undefined,
            filterSearch: true,
            render: (prjId: string) => (
                <span>{store.getProjectName(prjId)}</span>
            ),
            onCell: () => ({ "data-id-cells": "project" }) as object,
        },
        {
            title: "Type",
            dataIndex: "taskType",
            width: 100,
            sorter: true,
            sortOrder:
                store.sortOrder.field === "taskType"
                    ? store.sortOrder.order
                    : undefined,
            render: (taskType: string) => (
                <span>{store.getTaskName(taskType)}</span>
            ),
            onCell: () => ({ "data-id-cells": "type" }) as object,
        },
        {
            title: "Status",
            dataIndex: "statusName",
            width: 100,
            sorter: (a, b) =>
                Utils.safeStringLocaleCompare(a.statusName, b.statusName),
            sortOrder:
                store.sortOrder.field === "statusName"
                    ? store.sortOrder.order
                    : undefined,
            render: (statusName: string) => <span>{statusName}</span>,
            onCell: () => ({ "data-id-cells": "status" }) as object,
        },
        {
            title: "Due Date",
            dataIndex: "dueDate",
            width: 100,
            sorter: true,
            sortOrder:
                store.sortOrder.field === "dueDate"
                    ? store.sortOrder.order
                    : undefined,
            render: (dueDate: string, task: TaskListModel) => (
                <span
                    style={{
                        color:
                            (Utils.getDateTimestamp(dueDate, true) ??
                                Infinity) <= Date.now() && !task.isDone
                                ? "red"
                                : "",
                    }}
                >
                    {Utils.formatDateStringShort(
                        dueDate,
                        true,
                        true,
                        false,
                        true,
                    )}
                </span>
            ),
            onCell: () => ({ "data-id-cells": "due date" }) as object,
        },
        {
            title: "Attachments",
            dataIndex: "attachmentsCount",
            width: 100,
            sorter: (a, b) => a.attachmentsCount - b.attachmentsCount,
            sortOrder:
                store.sortOrder.field === "attachmentsCount"
                    ? store.sortOrder.order
                    : undefined,
            render: (attachmentsCount: string) => (
                <span>{attachmentsCount}</span>
            ),
            onCell: () => ({ "data-id-cells": "attachments" }) as object,
        },
        {
            title: "Owner",
            dataIndex: "createdBy",
            width: 100,
            sorter: true,
            sortOrder:
                store.sortOrder.field === "createdBy"
                    ? store.sortOrder.order
                    : undefined,
            render: (createdBy: string, task: TaskListModel) => {
                return (
                    <Observer>
                        {() => (
                            <div
                                style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                <ActorBadge
                                    actor={task.createdByActor}
                                    legacyUserId={createdBy}
                                    legacyUserName={
                                        store.allUsersFullNameResolver[
                                            createdBy
                                        ]
                                    }
                                    store={store}
                                    size={24}
                                    style={{
                                        marginRight: 0,
                                        verticalAlign: "middle",
                                    }}
                                />
                            </div>
                        )}
                    </Observer>
                );
            },
            onCell: () => ({ "data-id-cells": "Owner" }) as object,
        },
        {
            title: "Date Created",
            dataIndex: "createDate",
            width: 100,
            sorter: true,
            sortOrder:
                store.sortOrder.field === "createDate"
                    ? store.sortOrder.order
                    : undefined,
            render: (date: string) => {
                const formattedDate = Utils.formatDateStringShort(
                    date,
                    true,
                    true,
                );
                return (
                    <Tooltip title={formattedDate}>
                        <span>{formattedDate}</span>
                    </Tooltip>
                );
            },
            onCell: (record: TaskListModel) =>
                ({
                    "data-id-cells": "Date Created",
                    "data-id-timestamp": new Date(
                        record.createDate,
                    ).toISOString(),
                }) as object,
        },
        {
            title: "Assigned To",
            dataIndex: "assignedTo",
            width: 100,
            sorter: (a, b) =>
                Utils.safeStringLocaleCompare(a.assignedTo, b.assignedTo),
            sortOrder:
                store.sortOrder.field === "assignedTo"
                    ? store.sortOrder.order
                    : undefined,
            render: (userId: string) => {
                const isDeletedUser = store.userProfiles.some(
                    (u) => u.userId === userId && u.isDeleted,
                );
                return (
                    userId && (
                        <Observer>
                            {() => (
                                <div className="assigned-to">
                                    {getAvatar(userId)}
                                    <span style={{ verticalAlign: "middle" }}>
                                        {store.usersLoaded
                                            ? `${store.allUsersFullNameResolver[userId]}`
                                            : ""}
                                    </span>
                                    <span
                                        style={{
                                            verticalAlign: "middle",
                                            color: "red",
                                        }}
                                    >
                                        {isDeletedUser ? " (Inactive)" : ""}
                                    </span>
                                </div>
                            )}
                        </Observer>
                    )
                );
            },
            onCell: () => ({ "data-id-cells": "assigned to" }) as object,
        },
        {
            title: "Priority",
            dataIndex: "priority",
            width: 100,
            sorter: (a, b) => Utils.priorityCompare(a.priority, b.priority),
            sortOrder:
                store.sortOrder.field === "priority"
                    ? store.sortOrder.order
                    : undefined,
            render: (priority: string, task: TaskListModel) => {
                return (
                    <Select
                        className={`priority-select ${priority}`}
                        value={priority}
                        style={{ width: "100%", minWidth: 115 }}
                        onChange={(option: unknown) =>
                            store.updateField(task.id, "Priority", option)
                        }
                    >
                        <Select.Option value="LOW">
                            <Tag color="#9BA0AA" className="alpha-info-tag">
                                Low
                            </Tag>
                        </Select.Option>
                        <Select.Option value="MEDIUM">
                            <Tag color="#FADB14" className="alpha-info-tag">
                                Medium
                            </Tag>
                        </Select.Option>
                        <Select.Option value="HIGH">
                            <Tag color="#F5222D" className="alpha-info-tag">
                                High
                            </Tag>
                        </Select.Option>
                    </Select>
                );
            },
            onCell: () => ({ "data-id-cells": "priority" }) as object,
        },
    ];

    const metadataColumns = store.tableMetaData.map((m) => ({
        key: m.name,
        title: m.title,
        dataIndex: "metadata",
        width: 200,
        sorter: m.isMultiValue ? false : true,
        sortOrder:
            !m.isMultiValue && store.sortOrder.field === `metadata.${m.name}`
                ? store.sortOrder.order
                : undefined,
        render: (metadata: TaskMetadata | null) => {
            const value = Utils.getMetadataValue(
                m,
                metadata,
                store.tableMetaData,
            );
            const displayValue = Utils.getMetadataParseValue(value, m);
            return <span>{String(displayValue ?? "")}</span>;
        },
        onCell: () => ({ "data-id-cells": m.title }) as object,
    }));

    const [columns, setColumns] = useState<TableColumnsType<TaskListModel>>([
        ...allCollumns,
        ...metadataColumns,
    ]);

    React.useEffect(() => {
        let filteredCols = store.isMetadataViewEnabled
            ? [allCollumns[0], allCollumns[1], ...metadataColumns]
            : [...allCollumns, ...metadataColumns];
        filteredCols = store.taskColumns.length
            ? (store.filteredTaskColumns
                  .filter((x) => x.isVisible)
                  .map((x) => {
                      const filtered = filteredCols.find(
                          (col) => col.title === x.name,
                      );
                      filtered!.width = x.width || 100;
                      return filtered;
                  })
                  .filter((x) => x) as ColumnProps<TaskListModel>[])
            : filteredCols;
        setColumns(filteredCols);
    }, [
        store.taskColumns,
        store.isMetadataViewEnabled,
        store.metadataViewColumns,
    ]);

    const getAvatar = (userId: string) => {
        return (
            <TaskUserProfilePicture
                style={{ marginRight: 10, verticalAlign: "middle" }}
                store={store}
                size={24}
                userId={userId}
                userName={store.allUsersFullNameResolver[userId]}
            />
        );
    };

    const handleSubtasksClick = (task: TaskListModel) => {
        taskPreviewStore.togglePreview(task.id, ScrollToSections.Subtasks);
    };

    const getIcons = (task: TaskListModel) => {
        const icons = [];

        const handleAttachmentsClick = () => {
            taskPreviewStore.setSectionActiveKey("attachments", true);
            taskPreviewStore.togglePreview(
                task.id,
                ScrollToSections.Attachments,
            );
        };

        const subTasksСount = task.subTaskIds?.length;
        if (subTasksСount) {
            icons.push(
                <div
                    key="subtasks"
                    className="info-sub-container"
                    onClick={() => handleSubtasksClick(task)}
                >
                    <Tooltip title="Subtasks">
                        <i className="alpha-icon checked-bordered xxxs" />
                    </Tooltip>
                    <span style={{ fontSize: 10, color: "#9BA0AA" }}>
                        {subTasksСount}
                    </span>
                </div>,
            );
        }
        if (task.attachmentsCount) {
            icons.push(
                <div key="attachments" className="info-sub-container">
                    <Tooltip title="Attachments">
                        <i
                            className="alpha-icon attachment xxxs"
                            onClick={handleAttachmentsClick}
                        />
                    </Tooltip>
                    <span style={{ fontSize: 10, color: "#9BA0AA" }}>
                        {task.attachmentsCount}
                    </span>
                </div>,
            );
        }

        return icons;
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    const handleResize: Function =
        (index: number) =>
        (_: React.SyntheticEvent<Element>, { size }: ResizeCallbackData) => {
            const newColumns = [...columns];
            newColumns[index] = {
                ...newColumns[index],
                width: size.width,
            };
            setColumns(newColumns);
            store.handleColumnWidthChange(
                columns[index].title! as string,
                size.width,
            );
        };

    const getSortOrder = (col: ColumnProps<TaskListModel>) => {
        if (col.dataIndex === "metadata") {
            return `metadata.project.${col.key}` === store.sortOrder.field
                ? store.sortOrder.order
                : undefined;
        }
        return col.dataIndex === store.sortOrder.field
            ? store.sortOrder.order
            : undefined;
    };

    const mergedColumns = columns.map<TableColumnsType<TaskListModel>[number]>(
        (col, index) => ({
            ...col,
            sortOrder: getSortOrder(col as ColumnProps<TaskListModel>),
            sorter: true,
            onHeaderCell: (
                column: TableColumnsType<TaskListModel>[number] & {
                    dataIndex: string;
                },
            ) => ({
                width: column.width,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onResize: handleResize(index) as React.ReactEventHandler<any>,
            }),
        }),
    );

    const handleRowClick = (
        event: React.SyntheticEvent<Element, Event>,
        record: TaskListModel,
    ) => {
        if (hasSelectedText()) {
            return;
        }

        const element = event.target as Element;
        if (element.matches("td")) {
            taskPreviewStore.togglePreview(record.id);
        }
    };

    const getRowClassName = (task: TaskListModel) => {
        const classNames = [];

        if (store.taskStatuses.find((t) => t.id === task.status)?.isFinal) {
            classNames.push("closed");
        }

        if (selectedTaskId === task.id) {
            classNames.push("selected");
        }

        return classNames.join(" ");
    };

    const handlePageChange = async (page: number, pageSize: number) => {
        store.setCurrentPage(page);
        store.setPageSize(pageSize);
        store.setTasksIsLoading(true);
        store.unSelectAllRows();
        if (activityType) {
            await store.getTasksByActivityType(
                activityType,
                store.selectedProject,
                activityPeriod!,
            );
        } else if (widgetId && !value) {
            await store.getTasksByGroupedWidgetId(
                widgetId,
                store.selectedProject,
            );
        } else if (widgetId && value) {
            await store.getTasksByPieChartSection(
                widgetId,
                store.selectedProject,
                value,
            );
        } else {
            await store.loadTasks();
        }
        store.setTasksIsLoading(false);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleTableChange = async (pag: any, filt: any, sorter: any) => {
        if (
            store.sortOrder.field === sorter.field &&
            store.sortOrder.order === sorter.order
        ) {
            return;
        }
        store.setTasksIsLoading(true);
        store.unSelectAllRows();
        const columnTitle =
            sorter.field === "metadata"
                ? store.tableMetaData.find((s) => s.name === sorter.columnKey)!
                      .name
                : sorter.columnKey;
        if (!sorter.column && store.sortOrder.field !== "createDate") {
            store.setSortOrder({ field: "createDate", order: "descend" });
        } else {
            store.setSortOrder({
                field:
                    sorter.field === "metadata"
                        ? `metadata.project.${columnTitle}`
                        : sorter.field,
                order: sorter.order,
            });
        }
        if (widgetId && !value) {
            await store.getTasksByGroupedWidgetId(
                widgetId,
                store.selectedProject,
            );
        } else if (value) {
            await store.getTasksByPieChartSection(
                widgetId!,
                store.selectedProject,
                value,
            );
        } else if (activityType) {
            await store.getTasksByActivityType(
                activityType,
                store.selectedProject,
                activityPeriod!,
            );
        } else {
            await store.loadTasks();
        }
        store.setTasksIsLoading(false);
    };

    return (
        <div className="tasks-list" data-id="task-list-root">
            <Card
                style={{ border: "none", height: "100%" }}
                className="content-card shadow table-content"
            >
                <Row>
                    <Col span={24}>
                        <Table
                            data-id={"table-task-list"}
                            className="nested-table alpha-table no-border-top fixed sm-rows lg-title"
                            rowSelection={{
                                selectedRowKeys: store.selectedRows,
                                onChange: (selectedRowKeys, selectedRows) => {
                                    store.setSelectedRows(
                                        selectedRowKeys as string[],
                                    );
                                    store.setSelectedRowModels(selectedRows);
                                },
                                columnWidth: 60,
                                // TODO: Looks ugly ${record.parentTask?.alphaId.prefix}. We should introduce task as a class and use dedicated methods
                                renderCell: (
                                    val: boolean,
                                    record: TaskListModel,
                                    index: number,
                                    originNode: React.ReactNode,
                                ) => {
                                    return (
                                        <div>
                                            <div className="subtask-indicator">
                                                {record.taskId ? (
                                                    <i className="alpha-icon subtask-icon xs" />
                                                ) : (
                                                    ""
                                                )}
                                            </div>
                                            {originNode}
                                        </div>
                                    );
                                },
                            }}
                            rowKey={(r) => r.id}
                            rowClassName={getRowClassName}
                            style={{ borderTop: "none", width: "100%" }}
                            onRow={(record) => {
                                return {
                                    onClick: (e) => handleRowClick(e, record),
                                };
                            }}
                            components={{
                                header: {
                                    cell: ResizableTitle,
                                },
                            }}
                            columns={mergedColumns}
                            loading={store.tasksLoading}
                            onChange={handleTableChange}
                            dataSource={store.tasks}
                            pagination={{
                                defaultPageSize: store.pageSize,
                                total: store.tasksCount,
                                onChange: handlePageChange,
                                current: store.currentPage,
                            }}
                            scroll={{ y: `calc(100vh - ${headerHeight}px)` }}
                        />
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default observer(TaskList);
